import { queryClient } from "@/app/providers/query-client/QueryClientProvider";
import useCreateOrder from "@/shared/hooks/useCreateOrder";
import useGetBranch from "@/shared/hooks/useGetBranch";
import useGetEmployeeSchedule from "@/shared/hooks/useGetEmployeeSchedule";
import { useSteps } from "@/shared/hooks/useGetSteps";
import { UiInput } from "@2group/ui";
import { Button, Divider, Flex, message, Typography } from "antd";
import dayjs from "dayjs";
import { useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type { FormValues } from "../../model/form.schema";
import useDetailsScreenStyles from "./DetailsScreen.styles";

const DetailsScreen = () => {
  // ! ||--------------------------------------------------------------------------------
  // ! ||                                      hooks
  // ! ||--------------------------------------------------------------------------------

  const { styles, cx } = useDetailsScreenStyles();

  const { goStep, setSteps } = useSteps();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<FormValues>();
  const { branchData } = useGetBranch();

  const date = useWatch({ control, name: "date" });
  const end_time = useWatch({ control, name: "end_time" });
  const start_time = useWatch({ control, name: "start_time" });
  const duration = useWatch({ control, name: "duration" });
  const employee_id = useWatch({ control, name: "employee_id" });

  const { employeeScheduleData } = useGetEmployeeSchedule({
    date: date || dayjs(),
    duration: duration || branchData?.branch.online_booking_durations[0] || 30,
  });

  const { loading, mutateAsync } = useCreateOrder({
    onError: (e) => {
      if (!e?.code) return;
      queryClient.invalidateQueries({ queryKey: ["branch", "employee-schedule", "schedule"] });

      if (e?.code === "VALIDATION_ERROR" && e?.message) {
        message.error(e?.message);
        goStep("dateTime");

        return;
      }
      message.error("Произошла ошибка при записи");
      goStep("dateTime");
    },
    onSuccess: () => {
      message.success("Запись успешно создана");
      setSteps((prev) =>
        prev.map((step) => {
          if (step.status === "process") {
            return {
              ...step,
              status: "finish",
            };
          }
          return step;
        }),
      );
    },
  });

  // ! ||--------------------------------------------------------------------------------
  // ! ||                                      Logic
  // ! ||--------------------------------------------------------------------------------

  const onSave = async (values: FormValues) => {
    await mutateAsync({
      ...values,
      date: values.date.format("YYYY-MM-DD"),
    });
  };

  const currEmployee = useMemo(
    () => employeeScheduleData?.employees.find((emp) => emp.employee.id === employee_id),
    [employee_id, employeeScheduleData],
  );

  // ! ||--------------------------------------------------------------------------------
  // ! ||                                      render
  // ! ||--------------------------------------------------------------------------------

  return (
    <Flex className={styles.screen} vertical gap={8}>
      <Flex vertical className="bodyWrapper" gap={16}>
        <Typography.Title className="title" level={4}>
          Детали записи
        </Typography.Title>
        <Divider className="divider" />
        <Flex className="details" vertical gap={12}>
          <Flex className="detailsItem" gap={8} align="center" onClick={() => goStep("dateTime")}>
            <svg
              className="icon"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="22"
              viewBox="0 0 20 22"
              fill="none"
            >
              <path
                d="M5.75 0.75V4.75"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.75 0.75V4.75"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M16.75 2.75H2.75C1.64543 2.75 0.75 3.64543 0.75 4.75V18.75C0.75 19.8546 1.64543 20.75 2.75 20.75H16.75C17.8546 20.75 18.75 19.8546 18.75 18.75V4.75C18.75 3.64543 17.8546 2.75 16.75 2.75Z"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M0.75 8.75H18.75"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5.75 12.75H5.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M9.75 12.75H9.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.75 12.75H13.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5.75 16.75H5.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M9.75 16.75H9.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.75 16.75H13.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <Flex vertical className="detailsBody">
              <Typography.Text className="detailsTitle">
                {start_time} - {end_time}
              </Typography.Text>
              <Typography.Text className="detailsText">{date.format("dddd, D MMMM")}</Typography.Text>
            </Flex>
            <svg
              className="edit"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M21.1739 6.81238C21.7026 6.2838 21.9997 5.56685 21.9998 4.81923C21.9999 4.07162 21.703 3.35459 21.1744 2.82588C20.6459 2.29717 19.9289 2.00009 19.1813 2C18.4337 1.99991 17.7166 2.2968 17.1879 2.82538L3.84193 16.1744C3.60975 16.4059 3.43805 16.6909 3.34193 17.0044L2.02093 21.3564C1.99509 21.4429 1.99314 21.5347 2.01529 21.6222C2.03743 21.7097 2.08285 21.7896 2.14673 21.8534C2.21061 21.9172 2.29055 21.9624 2.37809 21.9845C2.46563 22.0065 2.55749 22.0044 2.64393 21.9784L6.99693 20.6584C7.3101 20.5631 7.59511 20.3925 7.82693 20.1614L21.1739 6.81238Z"
                stroke="#8C8C8C"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path d="M15 5L19 9" stroke="#8C8C8C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </Flex>
          <Flex className="detailsItem" gap={8} align="center" onClick={() => goStep("employee")}>
            <svg
              className="icon"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 13C14.7614 13 17 10.7614 17 8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8C7 10.7614 9.23858 13 12 13Z"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M20 21C20 18.8783 19.1571 16.8434 17.6569 15.3431C16.1566 13.8429 14.1217 13 12 13C9.87827 13 7.84344 13.8429 6.34315 15.3431C4.84285 16.8434 4 18.8783 4 21"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <Flex vertical className="detailsBody">
              <Typography.Text className="detailsTitle">
                {cx(
                  currEmployee?.employee.first_name,
                  currEmployee?.employee.middle_name,
                  currEmployee?.employee.last_name,
                )}
              </Typography.Text>
              <Typography.Text className="detailsText">
                {currEmployee?.employee.specialties
                  .map((sp) => sp.name)
                  .filter((i) => i.trim())
                  .join(", ")}
              </Typography.Text>
            </Flex>
            <svg
              className="edit"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M21.1739 6.81238C21.7026 6.2838 21.9997 5.56685 21.9998 4.81923C21.9999 4.07162 21.703 3.35459 21.1744 2.82588C20.6459 2.29717 19.9289 2.00009 19.1813 2C18.4337 1.99991 17.7166 2.2968 17.1879 2.82538L3.84193 16.1744C3.60975 16.4059 3.43805 16.6909 3.34193 17.0044L2.02093 21.3564C1.99509 21.4429 1.99314 21.5347 2.01529 21.6222C2.03743 21.7097 2.08285 21.7896 2.14673 21.8534C2.21061 21.9172 2.29055 21.9624 2.37809 21.9845C2.46563 22.0065 2.55749 22.0044 2.64393 21.9784L6.99693 20.6584C7.3101 20.5631 7.59511 20.3925 7.82693 20.1614L21.1739 6.81238Z"
                stroke="#8C8C8C"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path d="M15 5L19 9" stroke="#8C8C8C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </Flex>
        </Flex>
      </Flex>
      <Flex vertical className="bodyWrapper" gap={16}>
        <Controller
          name="customer.first_name"
          render={({ field }) => (
            <UiInput
              required
              error={errors.customer?.first_name}
              label="Имя"
              {...field}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />

        <Controller
          name="customer.phone_number"
          render={({ field }) => (
            <UiInput.PhoneNumber
              required
              label="Номер телефона"
              {...field}
              error={errors.customer?.phone_number}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="customer.last_name"
          render={({ field }) => (
            <UiInput
              error={errors.customer?.last_name}
              label="Фамилия"
              {...field}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="reason"
          render={({ field }) => (
            <UiInput
              error={errors.reason}
              label="Причина обращения"
              {...field}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Flex>
      <Flex className={cx("full-width", styles.footer)}>
        <Button
          type="primary"
          loading={loading}
          className="full-width"
          htmlType="submit"
          onClick={handleSubmit(onSave)}
          disabled={!employee_id}
        >
          Записаться
        </Button>
      </Flex>
    </Flex>
  );
};
export default DetailsScreen;
